// Navigation graph + pathfinding over a generated level.
//
// The graph is exactly the SOLVABILITY graph the checker uses: cells are nodes, and an edge
// exists only where a player can actually walk (stepBetween climbable AND no curb wall). So
// any path found here is one the solvability checker already guaranteed is traversable — the
// nav and the "is this level winnable" proof can never disagree.

import { CELL, topY } from "../config";
import { DVEC, type GeneratedLevel, type Tile, type LaneDiamond } from "../map/grammar";
import {
  stepBetween,
  edgeKey,
  edgeAxisOf,
  computeCurbs,
  rampSurfaceHeight,
} from "../map/geometry";

export interface Pt {
  x: number;
  y: number;
  z: number;
}

export interface NavGraph {
  adj: Map<string, string[]>;
  point: Map<string, Pt>; // cell-center world point (feet height)
  level: Map<string, number>; // cell top-surface level (ramp cells store their exit level)
  ramp: Map<string, Tile>; // cell -> ramp tile (for sloped height lookup)
  startKeys: string[]; // start pad cells (blue spawn)
  coreKeys: string[]; // enemy-core cells (red spawn)
  lanes: LaneDiamond[]; // lane splits in spine order (each = K parallel sub-routes' gate cells)
  /** Ground height at an arbitrary world XZ (ramp-interpolated), or null off the map. */
  heightAt(x: number, z: number): number | null;
  // ---- optional TERRA (open battlefield) extensions ----
  /** Shot-occlusion surface: real ground height even over flat hazards (lava/water/crevasse), null
   *  only outside the world. When present, LOS/bullet code samples THIS, so flat hazards block
   *  walking but never shots. */
  shotHeightAt?(x: number, z: number): number | null;
  /** Per-cell traversal cost (present ⇒ findPath runs weighted): open ~1, lava ~8, shallows ~2. */
  cost?: Map<string, number>;
  /** Terra string-pull test (walkability + climb) — replaces the tile band-equality LOS, which
   *  checkerboards on smooth slopes. */
  smoothLos?(a: Pt, b: Pt): boolean;
}

const key = (cx: number, cz: number): string => `${cx},${cz}`;

export function buildNav(lvl: GeneratedLevel): NavGraph {
  const cells = lvl.cells;
  const { blocked } = computeCurbs(cells);

  const ramp = new Map<string, Tile>();
  for (const t of lvl.tiles) {
    if (t.kind !== "ramp") continue;
    const cx0 = Math.round(t.x0 / CELL);
    const cx1 = Math.round(t.x1 / CELL);
    const cz0 = Math.round(t.z0 / CELL);
    const cz1 = Math.round(t.z1 / CELL);
    for (let cx = cx0; cx < cx1; cx++)
      for (let cz = cz0; cz < cz1; cz++) ramp.set(key(cx, cz), t);
  }

  const adj = new Map<string, string[]>();
  const point = new Map<string, Pt>();
  const level = new Map<string, number>();
  const startKeys: string[] = [];
  const coreKeys: string[] = [];

  for (const [k, info] of cells) {
    const [cx, cz] = k.split(",").map(Number);
    const wx = (cx + 0.5) * CELL;
    const wz = (cz + 0.5) * CELL;
    const rt = ramp.get(k);
    point.set(k, { x: wx, y: rt ? rampSurfaceHeight(rt, wx, wz) : topY(info.level), z: wz });
    level.set(k, info.level);
    if (info.kind === "start") startKeys.push(k);
    if (info.kind === "core") coreKeys.push(k);

    const nbrs: string[] = [];
    for (let d = 0; d < 4; d++) {
      const [dx, dz] = DVEC[d];
      const nk = key(cx + dx, cz + dz);
      const b = cells.get(nk);
      if (!b) continue;
      if (!stepBetween(info, b, edgeAxisOf(d))) continue;
      if (blocked.has(edgeKey(cx, cz, d))) continue;
      nbrs.push(nk);
    }
    adj.set(k, nbrs);
  }

  const heightAt = (x: number, z: number): number | null => {
    const k = key(Math.floor(x / CELL), Math.floor(z / CELL));
    const rt = ramp.get(k);
    if (rt) return rampSurfaceHeight(rt, x, z);
    const l = level.get(k);
    return l === undefined ? null : topY(l);
  };

  return { adj, point, level, ramp, startKeys, coreKeys, lanes: lvl.lanes, heightAt };
}

/** BFS shortest path with randomized neighbor order — different fighters diverge through
 *  open rooms/arenas instead of all tracing the one canonical shortest line. When the graph
 *  carries per-cell costs (terra), runs weighted (Dijkstra) with a small random jitter instead,
 *  so lava is a detour worth pricing and marching columns still fan out. */
export function findPath(nav: NavGraph, startKey: string, goalKey: string): string[] | null {
  if (startKey === goalKey) return [startKey];
  if (nav.cost) return findPathWeighted(nav, startKey, goalKey);
  const prev = new Map<string, string>();
  const seen = new Set<string>([startKey]);
  const q = [startKey];
  let head = 0;
  while (head < q.length) {
    const k = q[head++];
    const nbrs = nav.adj.get(k) ?? [];
    const order = nbrs.slice();
    for (let i = order.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      [order[i], order[j]] = [order[j], order[i]];
    }
    for (const n of order) {
      if (seen.has(n)) continue;
      seen.add(n);
      prev.set(n, k);
      if (n === goalKey) {
        const path = [n];
        let c = n;
        while (c !== startKey) {
          c = prev.get(c)!;
          path.push(c);
        }
        return path.reverse();
      }
      q.push(n);
    }
  }
  return null;
}

/** Weighted uniform-cost search over nav.cost (binary-heap Dijkstra). Cost jitter (±12%) plays
 *  the same divergence role the BFS neighbor shuffle does — spawned columns fan out. */
function findPathWeighted(nav: NavGraph, startKey: string, goalKey: string): string[] | null {
  const dist = new Map<string, number>([[startKey, 0]]);
  const prev = new Map<string, string>();
  const done = new Set<string>();
  // binary min-heap of [f, key]
  const hk: string[] = [startKey];
  const hf: number[] = [0];
  const up = (i: number): void => {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (hf[p] <= hf[i]) break;
      [hf[p], hf[i]] = [hf[i], hf[p]]; [hk[p], hk[i]] = [hk[i], hk[p]];
      i = p;
    }
  };
  const down = (i: number): void => {
    for (;;) {
      const l = i * 2 + 1, r = l + 1;
      let m = i;
      if (l < hf.length && hf[l] < hf[m]) m = l;
      if (r < hf.length && hf[r] < hf[m]) m = r;
      if (m === i) break;
      [hf[m], hf[i]] = [hf[i], hf[m]]; [hk[m], hk[i]] = [hk[i], hk[m]];
      i = m;
    }
  };
  while (hk.length) {
    const k = hk[0], d = hf[0];
    const lk = hk.pop()!, lf = hf.pop()!;
    if (hk.length) { hk[0] = lk; hf[0] = lf; down(0); }
    if (done.has(k)) continue;
    done.add(k);
    if (k === goalKey) {
      const path = [k];
      let c = k;
      while (c !== startKey) { c = prev.get(c)!; path.push(c); }
      return path.reverse();
    }
    for (const n of nav.adj.get(k) ?? []) {
      if (done.has(n)) continue;
      const step = (nav.cost!.get(n) ?? 1) * (0.94 + Math.random() * 0.24);
      const nd = d + step;
      if (nd < (dist.get(n) ?? Infinity)) {
        dist.set(n, nd);
        prev.set(n, k);
        hk.push(n); hf.push(nd); up(hk.length - 1);
      }
    }
  }
  return null;
}

/** Straight line-of-sight between two same-level cells (sampled). Curbs never sit inside a
 *  same-level region, so "all cells on the line are present at this level" is sufficient. */
function losClear(nav: NavGraph, a: Pt, b: Pt, lvl: number): boolean {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const dist = Math.hypot(dx, dz);
  const steps = Math.max(1, Math.ceil(dist / (CELL * 0.4)));
  for (let s = 1; s < steps; s++) {
    const x = a.x + (dx * s) / steps;
    const z = a.z + (dz * s) / steps;
    const k = key(Math.floor(x / CELL), Math.floor(z / CELL));
    if (nav.level.get(k) !== lvl) return false;
  }
  return true;
}

/** String-pull a cell path into a smooth polyline: within a constant-elevation run, skip
 *  ahead to the farthest cell with clear line-of-sight (removes the grid staircase); keep
 *  every ramp cell as a waypoint so height follows the slope. */
export function smoothPath(nav: NavGraph, cellKeys: string[]): Pt[] {
  const n = cellKeys.length;
  const P = (i: number): Pt => nav.point.get(cellKeys[i])!;
  if (n <= 2) return cellKeys.map((_, i) => P(i));
  const out: Pt[] = [P(0)];
  let anchor = 0;
  while (anchor < n - 1) {
    const aLvl = nav.level.get(cellKeys[anchor])!;
    let far = anchor + 1;
    for (let j = anchor + 2; j < n; j++) {
      if (nav.smoothLos) {
        if (!nav.smoothLos(P(anchor), P(j))) break; // terra: walkability + climb along the segment
      } else {
        if (nav.level.get(cellKeys[j]) !== aLvl) break; // tiles: ramp / level change: stop
        if (!losClear(nav, P(anchor), P(j), aLvl)) break;
      }
      far = j;
    }
    out.push(P(far));
    anchor = far;
  }
  return out;
}

/** Route from `from` to `to` forced through the ordered `vias` cells (lane gates), smoothing each
 *  leg independently so the string-pull can never cut across a lane diamond and collapse the
 *  split. Returns the concatenated smooth polyline, or null if any leg is unreachable. */
export function routeVia(nav: NavGraph, from: string, vias: string[], to: string): Pt[] | null {
  const stops = [from, ...vias, to];
  const out: Pt[] = [];
  for (let i = 0; i < stops.length - 1; i++) {
    if (stops[i] === stops[i + 1]) continue; // degenerate leg (duplicate gate)
    const seg = findPath(nav, stops[i], stops[i + 1]);
    if (!seg) return null;
    const sm = smoothPath(nav, seg);
    if (out.length) sm.shift(); // drop the duplicated join point
    out.push(...sm);
  }
  return out.length ? out : null;
}

/** The ordered lane-gate cells a unit should be forced through, so the army spreads evenly across
 *  every diamond's parallel sub-routes. `forward` = traveling start->core (blue); red travels the
 *  spine in reverse. `counter` is a per-team spawn index → round-robin lane pick, staggered per
 *  diamond so successive units don't all clump in the same lane. Empty when the level has no lanes. */
export function laneVias(nav: NavGraph, forward: boolean, counter: number): string[] {
  const diamonds = forward ? nav.lanes : [...nav.lanes].reverse();
  const vias: string[] = [];
  for (let di = 0; di < diamonds.length; di++) {
    const K = diamonds[di].lanes.length;
    if (K === 0) continue;
    const gates = diamonds[di].lanes[(((counter + di) % K) + K) % K];
    vias.push(...(forward ? gates : [...gates].reverse())); // entry->exit forward, exit->entry back
  }
  return vias;
}
